import { useMemo, useState } from 'react';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/Card';
import { specOf } from '@/domain/catalog';
import { assess } from '@/domain/engine';
import { createComponent } from '@/domain/project';
import { formatCurrency, formatMass, formatPercent, formatRatio } from '@/lib/format';
import type { ComponentInstance, ComponentKind, Project } from '@/domain/types';
import { ComponentFields } from '@/features/editor/ComponentFields';

interface WizardStep {
  id: string;
  title: string;
  hint: string;
  kinds: ComponentKind[];
  /** 该步骤的引导文案 */
  tips: string[];
}

const STEPS: readonly WizardStep[] = [
  {
    id: 'frame',
    title: '机体',
    hint: '第 1 步 / 共 6 步',
    kinds: ['frame'],
    tips: ['机架重量是起飞质量的一部分，先填个大概值也可以。'],
  },
  {
    id: 'power',
    title: '动力组',
    hint: '第 2 步 / 共 6 步',
    kinds: ['motor', 'esc', 'propeller'],
    tips: [
      '电机最大推力最好填实测值；没有实测值就填 KV、桨径、螺距与电芯数，系统会给出经验估算。',
      '电调的持续电流要 ≥ 单电机最大电流。',
    ],
  },
  {
    id: 'energy',
    title: '能源',
    hint: '第 3 步 / 共 6 步',
    kinds: ['battery'],
    tips: ['容量与电芯数决定电压与续航；放电倍率用于校验满油门电流是否够用。'],
  },
  {
    id: 'avionics',
    title: '航电',
    hint: '第 4 步 / 共 6 步',
    kinds: ['fc', 'vtx', 'rx'],
    tips: ['这些部件的重量同样计入起飞质量，价格计入总金额。'],
  },
  {
    id: 'payload',
    title: '载荷',
    hint: '第 5 步 / 共 6 步',
    kinds: ['payload'],
    tips: ['相机 / 云台 / 任务设备都算载荷，可以再加一件。'],
  },
  {
    id: 'review',
    title: '复核',
    hint: '第 6 步 / 共 6 步',
    kinds: [],
    tips: ['看一遍结论：不通过的项目会直接标出缺什么参数。'],
  },
];

export interface SetupWizardProps {
  project: Project;
  onUpdate: (updater: (project: Project) => Project) => void;
  onFinish: () => void;
  onExit: () => void;
}

/**
 * 新建空白项目的引导向导。
 *
 * 每一步都可以跳过、每个字段都可以留空；填到一半也能直接看结果，
 * 因此不强制线性流程，只把「该填什么」按顺序摆出来。
 */
export function SetupWizard({ project, onUpdate, onFinish, onExit }: SetupWizardProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const step = STEPS[stepIndex] ?? STEPS[0];
  const assessment = useMemo(() => assess(project), [project]);

  const items = useMemo(
    () => project.components.filter((component) => step?.kinds.includes(component.kind)),
    [project.components, step],
  );

  const updateComponent = (id: string, next: ComponentInstance) => {
    onUpdate((current) => ({
      ...current,
      components: current.components.map((component) => (component.id === id ? next : component)),
    }));
  };

  const addComponent = (kind: ComponentKind) => {
    onUpdate((current) => ({
      ...current,
      components: [...current.components, createComponent(kind)],
    }));
  };

  if (!step) return null;

  const isLast = stepIndex === STEPS.length - 1;

  return (
    <div className="flex flex-col gap-3">
      <Card>
        <CardBody className="flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="label-mono">{step.hint}</p>
              <h2 className="mt-0.5 truncate text-base font-semibold text-fg">{step.title}</h2>
            </div>
            <Button size="md" variant="ghost" onClick={onExit}>
              退出引导
            </Button>
          </div>

          <div className="flex gap-1" role="progressbar" aria-valuemin={1} aria-valuemax={STEPS.length} aria-valuenow={stepIndex + 1}>
            {STEPS.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`跳到${item.title}`}
                onClick={() => setStepIndex(index)}
                className={[
                  'h-1.5 flex-1 rounded-full transition-colors',
                  index <= stepIndex ? 'bg-accent' : 'bg-surface-3',
                ].join(' ')}
              />
            ))}
          </div>

          <ul className="flex flex-col gap-1">
            {step.tips.map((tip) => (
              <li key={tip} className="text-[11px] leading-relaxed text-fg-muted">
                <span aria-hidden className="mr-1 text-fg-subtle">
                  ·
                </span>
                {tip}
              </li>
            ))}
          </ul>
        </CardBody>
      </Card>

      {step.id === 'review' ? (
        <Card>
          <CardHeader
            actions={
              <Badge tone={assessment.verdict.tone} solid={assessment.verdict.level !== 'unknown'}>
                {assessment.verdict.label}
              </Badge>
            }
          >
            <CardTitle subtitle="当前参数下算出来的结果">评估摘要</CardTitle>
          </CardHeader>
          <CardBody className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] text-fg-muted">预估起飞质量</span>
              <span className="tabular text-sm font-semibold text-fg">
                {formatMass(assessment.mass.value?.totalG ?? null).value}{' '}
                {formatMass(assessment.mass.value?.totalG ?? null).unit}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] text-fg-muted">预估总推力</span>
              <span className="tabular text-sm font-semibold text-fg">
                {formatMass(assessment.thrust.value?.totalG ?? null).value}{' '}
                {formatMass(assessment.thrust.value?.totalG ?? null).unit}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] text-fg-muted">有效推重比</span>
              <span className="tabular text-sm font-semibold text-fg">
                {formatRatio(assessment.twr.value?.effective ?? null)}
              </span>
            </div>
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] text-fg-muted">预估金额</span>
              <span className="tabular text-sm font-semibold text-fg">
                {formatCurrency(assessment.cost.value?.total ?? null)}
              </span>
            </div>
            <div className="col-span-2 flex flex-col gap-0.5 border-t border-line pt-2">
              <span className="text-[11px] text-fg-muted">数据完整度</span>
              <span className="tabular text-sm font-semibold text-fg">
                {formatPercent(assessment.completeness * 100, 0)}
              </span>
              {assessment.missingAll.length > 0 ? (
                <span className="mt-1 text-[11px] leading-relaxed text-fg-subtle">
                  还有 {assessment.missingAll.length} 项参数没填，它们未计入计算。
                </span>
              ) : null}
            </div>
          </CardBody>
        </Card>
      ) : (
        <>
          {items.map((component) => (
            <Card key={component.id}>
              <CardHeader actions={<Badge>×{component.qty}</Badge>}>
                <CardTitle subtitle={specOf(component.kind).label}>
                  {component.label?.trim() || specOf(component.kind).label}
                </CardTitle>
              </CardHeader>
              <CardBody>
                <ComponentFields
                  component={component}
                  onChange={(next) => updateComponent(component.id, next)}
                />
              </CardBody>
            </Card>
          ))}

          {items.length === 0 ? (
            <Card>
              <CardBody className="flex flex-col items-start gap-2">
                <p className="text-xs text-fg-muted">清单里还没有该步骤的部件。</p>
                <div className="flex flex-wrap gap-2">
                  {step.kinds.map((kind) => (
                    <Button key={kind} size="lg" onClick={() => addComponent(kind)}>
                      + 添加{specOf(kind).label}
                    </Button>
                  ))}
                </div>
              </CardBody>
            </Card>
          ) : (
            <div className="flex flex-wrap gap-2">
              {step.kinds
                .filter((kind) => !items.some((component) => component.kind === kind))
                .map((kind) => (
                  <Button key={kind} size="lg" variant="default" onClick={() => addComponent(kind)}>
                    + 添加{specOf(kind).label}
                  </Button>
                ))}
              {step.id === 'payload' ? (
                <Button size="lg" variant="default" onClick={() => addComponent('payload')}>
                  + 再加一件载荷
                </Button>
              ) : null}
            </div>
          )}
        </>
      )}

      <div className="sticky bottom-0 z-10 -mx-4 flex items-center justify-between gap-2 border-t border-line bg-surface-0/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur md:-mx-6 md:px-6">
        <Button
          size="lg"
          variant="ghost"
          disabled={stepIndex === 0}
          onClick={() => setStepIndex((index) => Math.max(0, index - 1))}
        >
          上一步
        </Button>
        <div className="flex items-center gap-2">
          <Button size="lg" variant="ghost" onClick={onFinish}>
            跳过，直接看结果
          </Button>
          {isLast ? (
            <Button size="lg" variant="primary" onClick={onFinish}>
              完成
            </Button>
          ) : (
            <Button
              size="lg"
              variant="primary"
              onClick={() => setStepIndex((index) => Math.min(STEPS.length - 1, index + 1))}
            >
              下一步
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
